import { LaunchDarklyClientProvider } from '@openfeature/launchdarkly-client-provider'
import { InMemoryProvider, Provider } from '@openfeature/web-sdk'
import { getOpenFeatureSpecs } from './inMemory'
import {
  FeatureFlagProviderConfig,
  FeatureFlagProvider,
  FlagDefinitions,
} from './types'

/**
 * Creates a LaunchDarkly OpenFeature provider, or undefined if no launchDarklyClientSideId is configured.
 * @param config
 */
function makeLaunchDarklyProvider(
  config: FeatureFlagProviderConfig
): LaunchDarklyClientProvider | undefined {
  if (!config.launchDarklyClientSideId) {
    return undefined
  }
  return new LaunchDarklyClientProvider(config.launchDarklyClientSideId, {})
}

/**
 * Creates an OpenFeature provider based on config.
 * @param provider
 * @param flagDefinitions
 * @param config
 */
export function makeProvider(
  provider: FeatureFlagProvider,
  flagDefinitions: FlagDefinitions,
  config: FeatureFlagProviderConfig
): Provider | undefined {
  let openFeatureProvider
  switch (provider) {
    case FeatureFlagProvider.LAUNCH_DARKLY: {
      openFeatureProvider = makeLaunchDarklyProvider(config)
      break
    }
    case FeatureFlagProvider.LOCAL: {
      openFeatureProvider = new InMemoryProvider(
        getOpenFeatureSpecs(flagDefinitions, 'local')
      )
      break
    }
    default: {
      throw new Error(`Unsupported primary provider: ${provider}`)
    }
  }

  if (!openFeatureProvider && config.primaryProvider === provider) {
    throw new Error(
      `Primary provider ${config.primaryProvider} is not configured properly.`
    )
  }
  return openFeatureProvider
}

/**
 * Returns configured non-primary remote providers to use for comparison.
 * LOCAL is only included if it's the primary provider.
 * @param config
 * @param flagDefinitions
 */
export function makeProviders(
  config: FeatureFlagProviderConfig,
  flagDefinitions: FlagDefinitions
) {
  const providers: Partial<Record<FeatureFlagProvider, Provider>> = {}
  for (const providerName of Object.values(FeatureFlagProvider)) {
    if (
      providerName === FeatureFlagProvider.LOCAL &&
      providerName !== config.primaryProvider
    ) {
      continue
    }
    const provider = makeProvider(providerName, flagDefinitions, config)
    if (provider) {
      providers[providerName] = provider
    }
  }
  return providers
}
